import { md5 } from 'js-md5';
import { encodeBase64Bytes } from './base64';
import { encodeHexBytes } from './hex';

/**
 * Text hashing for `/tools/crypto/hash`.
 *
 * Algorithm picks:
 * - MD5 / SHA-1: kept for parity with legacy systems that still emit
 *   them (e.g. Maven artefact checksums, old auth schemes). Marked
 *   "broken" in UI copy, but hashing them is part of the workflow.
 * - SHA-256 / 384 / 512: covered by `crypto.subtle.digest`. Browsers
 *   ship Web Crypto since 2014 (modern Safari included), so no
 *   fallback path required for our user base.
 *
 * SHA-1 in WebCrypto is also supported and we prefer it over a JS
 * fallback for performance reasons.
 *
 * Output encodings: hex (lowercase), base64, base64url. Tests pin
 * the canonical NIST vectors, so future swaps to a different MD5 lib
 * will surface immediately.
 */

export type HashAlgo = 'MD5' | 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';
export type HashOutput = 'hex' | 'base64' | 'base64url';
export type HashCharset = 'utf-8' | 'ascii';

export const HASH_ALGOS: readonly HashAlgo[] = [
  'MD5',
  'SHA-1',
  'SHA-256',
  'SHA-384',
  'SHA-512',
] as const;

/** 10 MB — past this we ask the user to use the (future) hash-file tool. */
export const HASH_TEXT_CAP = 10 * 1024 * 1024;

export async function hashText(
  text: string,
  algo: HashAlgo,
  output: HashOutput,
  charset: HashCharset = 'utf-8',
): Promise<string> {
  if (text.length > HASH_TEXT_CAP) {
    throw new Error(
      `输入超过 ${(HASH_TEXT_CAP / 1024 / 1024).toFixed(0)} MB 上限，请使用 hash-file（待实现）`,
    );
  }

  const bytes = encodeText(text, charset);
  const digest = await digestBytes(bytes, algo);
  return encodeOutput(digest, output);
}

export async function hashAll(
  text: string,
  output: HashOutput,
  charset: HashCharset = 'utf-8',
): Promise<Record<HashAlgo, string>> {
  const entries = await Promise.all(
    HASH_ALGOS.map(async (algo) => [algo, await hashText(text, algo, output, charset)] as const),
  );
  return Object.fromEntries(entries) as Record<HashAlgo, string>;
}

function encodeText(text: string, charset: HashCharset): Uint8Array {
  if (charset === 'utf-8') return new TextEncoder().encode(text);
  // ASCII mode silently degrades non-ASCII to '?' rather than throwing
  // — the page warns the user via a banner.
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    bytes[i] = c < 0x80 ? c : 0x3f;
  }
  return bytes;
}

async function digestBytes(bytes: Uint8Array, algo: HashAlgo): Promise<Uint8Array> {
  if (algo === 'MD5') {
    // js-md5 returns a Uint8Array via `arrayBuffer()`.
    const out = md5.arrayBuffer(bytes);
    return new Uint8Array(out);
  }
  // crypto.subtle.digest accepts any BufferSource, but TS narrows
  // `Uint8Array<ArrayBufferLike>` too widely (including
  // SharedArrayBuffer). Re-wrap in a plain Uint8Array<ArrayBuffer> by
  // slicing the underlying buffer first — this also yields a tight
  // copy that doesn't pin the original.
  const tight = new Uint8Array(bytes.byteLength);
  tight.set(bytes);
  const digest = await crypto.subtle.digest(algo, tight);
  return new Uint8Array(digest);
}

function encodeOutput(bytes: Uint8Array, output: HashOutput): string {
  if (output === 'hex') return encodeHexBytes(bytes);
  if (output === 'base64') return encodeBase64Bytes(bytes);
  // base64url: encode as urlsafe + drop padding, matching common
  // JWT / OAuth conventions.
  return encodeBase64Bytes(bytes, { variant: 'urlsafe', noPadding: true });
}
